import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, of, pipe, switchMap, tap } from 'rxjs';
import { LeadBankService } from './lead-bank.service';

export interface LeadBankOption {
  acnId: string | undefined;
  name: string;
  email: string;
  lgoImg: string | undefined;
  domains: string[];
  raw: unknown;
}

interface LeadBankState {
  leadBanks: LeadBankOption[];
  leadBanksLoading: boolean;
  validationLoading: boolean;
  validationResult: {
    sts: string;
    correctedEmail?: string;
    existingEmailMatches?: string[];
    message?: string;
  } | null;
  submitLoading: boolean;
  submitResult: unknown;
  submitError: unknown;
  error: unknown;
}

const initialState: LeadBankState = {
  leadBanks: [],
  leadBanksLoading: false,
  validationLoading: false,
  validationResult: null,
  submitLoading: false,
  submitResult: null,
  submitError: null,
  error: null,
};

export const LeadBankStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(LeadBankService)) => ({
    loadLeadBanks: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { leadBanksLoading: true, error: null })),
        switchMap(() =>
          service.getLeadBanks().pipe(
            tap(leadBanks =>
              patchState(store, { leadBanks, leadBanksLoading: false })
            ),
            catchError(error => {
              patchState(store, { leadBanksLoading: false, error });
              return of(null);
            })
          )
        )
      )
    ),

    validateEmail: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { validationLoading: true, error: null })),
        switchMap(email =>
          service.validateEmail(email).pipe(
            tap(validationResult =>
              patchState(store, { validationResult, validationLoading: false })
            ),
            catchError(error => {
              patchState(store, { validationLoading: false, error });
              return of(null);
            })
          )
        )
      )
    ),

    submitExternalInvest: rxMethod<unknown>(
      pipe(
        tap(() =>
          patchState(store, {
            submitLoading: true,
            submitResult: null,
            submitError: null,
          })
        ),
        switchMap(payload =>
          service.submitExternalInvest(payload).pipe(
            tap(submitResult =>
              patchState(store, { submitResult, submitLoading: false })
            ),
            catchError(submitError => {
              patchState(store, { submitLoading: false, submitError });
              return of(null);
            })
          )
        )
      )
    ),

    clearValidation(): void {
      patchState(store, {
        validationResult: null,
        validationLoading: false,
        error: null,
      });
    },

    resetSubmit(): void {
      patchState(store, { submitResult: null, submitError: null });
    },
  }))
);
